import { useState } from 'react';
import { Loader2, Search, AlertTriangle, ShieldAlert, HelpCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function SignalBadge({ signal }) {
  if (signal === 'api_cancelled') {
    return (
      <Badge variant="destructive" className="text-[10px]">
        API cancelled
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700 dark:text-amber-300">
      Absent - verify
    </Badge>
  );
}

export function StaleConfirmedPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.staleConfirmedBookings();
      setResult(data);
      const count = data.candidates?.length || 0;
      if (count > 0) {
        toast.warning(`${count} stale-confirmed candidate(s) found`);
      } else {
        toast.success('No stale-confirmed bookings found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const apiCancelled = result?.candidates?.filter(c => c.signal === 'api_cancelled') || [];
  const absent = result?.candidates?.filter(c => c.signal === 'absent_from_api') || [];
  const couldNotVerify = result?.could_not_verify || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-500" />
          Stale Confirmed Detector
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Detects bookings stored as confirmed in the DB but cancelled (or absent)
          on the Uplisting API. Scope: check-out within last 90 days or check-in
          in the future. Read-only, no writes. Copy the uplisting_id into the
          Manual Cancel Booking card to act on flagged bookings.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {result && (
          <div className="space-y-5">
            {/* Summary counters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Confirmed in scope</p>
                <p className="text-xl font-bold tabular-nums">{result.confirmed_in_scope}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Properties checked</p>
                <p className="text-xl font-bold tabular-nums">{result.properties_checked}</p>
              </div>
              <div className="rounded-lg border p-3 border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">API cancelled</p>
                <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                  {apiCancelled.length}
                </p>
              </div>
              <div className="rounded-lg border p-3 border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Absent (verify)</p>
                <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                  {absent.length}
                </p>
              </div>
            </div>

            {/* API Cancelled table (high confidence, act) */}
            {apiCancelled.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <p className="text-sm font-medium">
                    API reports cancelled ({apiCancelled.length})
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  High confidence. These bookings are confirmed in the DB but the
                  Uplisting API returns a cancel status. Use the Manual Cancel
                  Booking card to process them.
                </p>
                <CandidateTable rows={apiCancelled} />
              </div>
            )}

            {/* Absent table (low confidence, verify first) */}
            {absent.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm font-medium">
                    Absent from API ({absent.length})
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Lower confidence. These bookings were not returned by the API
                  for their property/date window. Check the channel directly
                  before cancelling.
                </p>
                <CandidateTable rows={absent} />
              </div>
            )}

            {/* Could not verify */}
            {couldNotVerify.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Could not verify ({couldNotVerify.length} properties)
                  </p>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">ID</th>
                        <th className="text-right px-3 py-2 font-medium">Bookings</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {couldNotVerify.map((p, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 text-xs">{p.property_name}</td>
                          <td className="px-3 py-2 font-mono text-xs">{p.property_id}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{p.bookings_count}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{p.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No candidates */}
            {result.candidates?.length === 0 && couldNotVerify.length === 0 && (
              <p className="text-sm text-green-600 dark:text-green-400">
                All in-scope confirmed bookings match their API status. No action needed.
              </p>
            )}

            {/* Checked at */}
            <p className="text-[10px] text-muted-foreground">
              Checked at {result.checked_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CandidateTable({ rows }) {
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
            <th className="text-left px-3 py-2 font-medium">Guest</th>
            <th className="text-left px-3 py-2 font-medium">Property</th>
            <th className="text-left px-3 py-2 font-medium">Check-in</th>
            <th className="text-left px-3 py-2 font-medium">Check-out</th>
            <th className="text-left px-3 py-2 font-medium">Channel</th>
            <th className="text-right px-3 py-2 font-medium">Net Revenue</th>
            <th className="text-left px-3 py-2 font-medium">Signal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.uplisting_id} className="border-b last:border-0">
              <td className="px-3 py-2 font-mono text-xs">{c.uplisting_id}</td>
              <td className="px-3 py-2 text-xs">{c.guest_name}</td>
              <td className="px-3 py-2 text-xs">{c.property_name}</td>
              <td className="px-3 py-2 font-mono text-xs">{c.check_in}</td>
              <td className="px-3 py-2 font-mono text-xs">{c.check_out}</td>
              <td className="px-3 py-2 text-xs">{c.channel}</td>
              <td className="px-3 py-2 text-right tabular-nums text-xs">
                {c.net_revenue != null ? Number(c.net_revenue).toFixed(2) : '(null)'}
                {' '}{c.currency}
              </td>
              <td className="px-3 py-2">
                <SignalBadge signal={c.signal} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
